import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { MentorDto } from "../../types/membersTypes";
import { ArrowDropDown } from "@mui/icons-material";
import { Link } from "react-router-dom";
import useResponsive from "../../hooks/useResponsive";

interface MentorsAccordionProps {
  mentors: MentorDto[];
  mentorOptions: MentorDto[];
  mentorToAdd: MentorDto | null;
  setMentorToAdd: (mentor: MentorDto | null) => void;
  handleAddMentor: () => void;
  mentorToRemove: MentorDto | null;
  setMentorToRemove: (mentor: MentorDto | null) => void;
  handleRemoveMentor: () => void;
}
/**
 * Component to display the mentors of a trainee generation in an accordion
 * @param mentors - An array of mentors of type MentorDto to be displayed in the accordion
 * @param mentorOptions - An array of mentors of type MentorDto to be displayed in the mentor selection for adding a mentor
 * @param mentorToAdd - The mentor selected for adding to the generation
 * @param setMentorToAdd - A function to set the mentor selected for adding to the generation
 * @param handleAddMentor - A function to handle the addition of a mentor to the generation
 * @param mentorToRemove - The mentor selected for removing from the generation
 * @param setMentorToRemove - A function to set the mentor selected for removing from the generation
 * @param handleRemoveMentor - A function to handle the removal of a mentor from the generation
 * @returns An Accordion component displaying the mentors of a trainee generation, each mentor is displayed as a link to their profile page
 */
const MentorsAccordion = ({
  mentors,
  mentorOptions,
  mentorToAdd,
  setMentorToAdd,
  handleAddMentor,
  mentorToRemove,
  setMentorToRemove,
  handleRemoveMentor,
}: MentorsAccordionProps) => {
  const isMobile = useResponsive("down", "sm");
  return (
    <Accordion sx={{ marginBottom: 2 }}>
      <AccordionSummary expandIcon={<ArrowDropDown />} sx={{ fontWeight: "bold", fontSize: 20 }}>
        Mentoren:
      </AccordionSummary>
      <AccordionDetails>
        <Divider sx={{ marginBottom: 2 }} />
        {mentors.map((mentor, index) => (
          <span key={index}>
            <Link to={`/gesamtuebersicht/${mentor.memberId}`} style={{ textDecoration: "none", color: "inherit" }}>
              {mentor.firstname} {mentor.lastname}
            </Link>
            {index < mentors.length - 1 && ", "}
          </span>
        ))}

        <Stack direction={isMobile ? "column" : "row"} spacing={isMobile ? 2 : 10} sx={{ marginTop: 3 }}>
          <Stack
            direction={isMobile ? "column" : "row"}
            alignItems={isMobile ? "start" : "center"}
            spacing={isMobile ? 1 : 0}
          >
            <Typography variant="subtitle1" sx={{ mr: 1 }}>
              Mentor hinzufügen:
            </Typography>
            <Autocomplete
              renderInput={(params) => <TextField variant="outlined" {...params} label="Mitglied" />}
              options={mentorOptions}
              getOptionLabel={(option) => `${option.firstname} ${option.lastname}`}
              sx={{ width: 230 }}
              size="small"
              value={mentorToAdd}
              onChange={(event, newValue) => {
                setMentorToAdd(newValue);
              }}
            />
            <Button
              variant="contained"
              color="success"
              size="medium"
              sx={{ ml: isMobile ? 0 : 2 }}
              onClick={handleAddMentor}
              disabled={!mentorToAdd}
            >
              Hinzufügen
            </Button>
          </Stack>

          <Stack
            direction={isMobile ? "column" : "row"}
            alignItems={isMobile ? "start" : "center"}
            spacing={isMobile ? 1 : 0}
          >
            <Typography variant="subtitle1" sx={{ mr: 1 }}>
              Mentor löschen:
            </Typography>
            <Autocomplete
              renderInput={(params) => <TextField variant="outlined" {...params} label="Mitglied" />}
              options={mentors}
              getOptionLabel={(option) => `${option.firstname} ${option.lastname}`}
              sx={{ width: 230 }}
              size="small"
              value={mentorToRemove}
              onChange={(event, newValue) => {
                setMentorToRemove(newValue);
              }}
            />
            <Button
              variant="contained"
              color="error"
              size="medium"
              sx={{ ml: isMobile ? 0 : 2 }}
              onClick={handleRemoveMentor}
              disabled={!mentorToRemove}
            >
              Löschen
            </Button>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default MentorsAccordion;
